import { Head, Link } from '@inertiajs/react'
import { ArrowLeft, Building2, CalendarDays, CreditCard, MapPin, Trophy, Users2 } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import type { Breadcrumb } from '@/types/shared'

type SchoolDetail = {
  id: string
  name: string
  prefecture: string | null
  league: string | null
  selfIntroduction: string | null
  coachesCount: number
  iconUrl?: string | null
  paymentType: number | null
  createdAt: string
  updatedAt: string
}

type PlayerSchoolShowProps = {
  school: SchoolDetail
  breadcrumbs: Breadcrumb[]
}

export default function PlayerSchoolShow({ school, breadcrumbs }: PlayerSchoolShowProps) {
  return (
    <AppShell>
      <Head title={`${school.name}｜高校詳細`} />
      <PageHeader
        title={school.name}
        description="高校の基本情報と自己紹介を確認できます。"
        breadcrumbs={breadcrumbs}
        actions={
          <Button asChild variant="secondary" size="sm">
            <Link href="/player/schools">
              <ArrowLeft className="h-4 w-4" />
              一覧へ戻る
            </Link>
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
        <Card className="space-y-8" padding="lg">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-brand-500/20 text-brand-100">
              {school.iconUrl ? (
                <img src={school.iconUrl} alt={school.name} className="h-full w-full object-cover" />
              ) : (
                <Building2 className="h-9 w-9" />
              )}
            </div>
            <div className="min-w-0 space-y-3">
              <div>
                <p className="text-sm text-white/50">登録高校</p>
                <h2 className="mt-1 break-words text-2xl font-semibold text-white">{school.name}</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {school.prefecture ? <Badge variant="outline">{school.prefecture}</Badge> : null}
                {school.league ? <Badge variant="outline">{school.league}</Badge> : null}
              </div>
            </div>
          </div>

          <Section title="自己紹介">
            {school.selfIntroduction ? (
              <div className="whitespace-pre-wrap rounded-2xl border border-white/10 bg-white/5 p-5 text-sm leading-7 text-white/80">
                {school.selfIntroduction}
              </div>
            ) : (
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-sm text-white/50">
                自己紹介はまだ登録されていません。
              </div>
            )}
          </Section>
        </Card>

        <div className="space-y-6">
          <Card padding="lg">
            <Section title="基本情報">
              <InfoList
                items={[
                  { icon: <MapPin className="h-4 w-4" />, label: '都道府県', value: school.prefecture ?? '未設定' },
                  { icon: <Trophy className="h-4 w-4" />, label: '所属リーグ', value: school.league ?? '未設定' },
                  { icon: <Users2 className="h-4 w-4" />, label: '登録コーチ数', value: `${school.coachesCount}名` },
                  { icon: <CreditCard className="h-4 w-4" />, label: '支払い種別', value: formatPaymentType(school.paymentType) },
                ]}
              />
            </Section>
          </Card>

          <Card padding="lg">
            <Section title="登録情報">
              <InfoList
                items={[
                  { icon: <CalendarDays className="h-4 w-4" />, label: '登録日時', value: formatDateTime(school.createdAt) },
                  { icon: <CalendarDays className="h-4 w-4" />, label: '更新日時', value: formatDateTime(school.updatedAt) },
                ]}
              />
            </Section>
          </Card>
        </div>
      </div>
    </AppShell>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold text-white">{title}</h2>
      {children}
    </section>
  )
}

function InfoList({
  items,
}: {
  items: Array<{ icon: React.ReactNode; label: string; value: React.ReactNode }>
}) {
  return (
    <dl className="space-y-3">
      {items.map((item) => (
        <div key={item.label} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="mt-0.5 text-brand-300">{item.icon}</div>
          <div>
            <dt className="text-xs text-white/40">{item.label}</dt>
            <dd className="mt-1 break-words text-sm text-white/80">{item.value}</dd>
          </div>
        </div>
      ))}
    </dl>
  )
}

function formatPaymentType(value: number | null) {
  if (value === null) return '未設定'

  return `タイプ${value}`
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('ja-JP', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
